import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import {
  Dashboard,
  People,
  Business,
  Logout as LogoutIcon,
  PersonAdd,
  Receipt,
  Payment,
  Assignment,
  ContactMail,
  School,
} from '@mui/icons-material';
import InstituteLogo from '../InstituteLogo';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
interface SidebarProps {
  open: boolean;
  onClose: () => void;
  drawerWidth: number;
}

const Sidebar = ({ open, onClose, drawerWidth }: SidebarProps) => {
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('keepSignedIn');
    localStorage.removeItem('studentRegistrationNumber');
    navigate('/login?role=app');
  };
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogoClick = () => {
    // In admin panel, clicking logo should stay in admin dashboard
    navigate('/admin-dashboard');
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
  };

  const menuItems = [
    { id: 1, label: 'Dashboard', icon: <Dashboard />, path: '/admin-dashboard' },
    { id: 2, label: 'Add Student', icon: <PersonAdd />, path: '/admin/add-student' },
    { id: 3, label: 'Manage Students', icon: <People />, path: '/admin/students' },
    { id: 4, label: 'Add Center', icon: <Business />, path: '/admin/add-center' },
    { id: 5, label: 'Manage Centers', icon: <Business />, path: '/admin/centers' },
    { id: 6, label: 'Create Bill', icon: <Receipt />, path: '/admin/create-bill' },
    { id: 7, label: 'Manage Bills', icon: <Payment />, path: '/admin/bills' },
    { id: 8, label: 'Add Marksheet', icon: <Assignment />, path: '/admin/add-marksheet' },
    { id: 9, label: 'Course Management', icon: <School />, path: '/admin/courses' },
    { id: 11, label: 'Enquiry List', icon: <ContactMail />, path: '/admin/enquiry-list' },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
            transition: 'opacity 0.2s ease',
          }}
          onClick={handleLogoClick}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: '#ffffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InstituteLogo width={65} height={65} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: '#1e293b',
              fontSize: '1.1rem',
            }}
          >
            Admin Panel
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f5f9',
              color: '#1e293b',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Navigation Menu */}
      <List sx={{ flex: 1, pt: 2 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.id} disablePadding sx={{ px: 2, mb: 1 }}>
              <ListItemButton
                onClick={() => handleMenuItemClick(item.path)}
                sx={{
                  borderRadius: 2,
                  backgroundColor: isActive ? '#1e293b' : 'transparent',
                  color: isActive ? 'white' : '#64748b',
                  '&:hover': {
                    backgroundColor: isActive ? '#1e293b' : '#f1f5f9',
                    color: isActive ? 'white' : '#1e293b',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
              <ListItemIcon
                sx={{
                  color: 'inherit',
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.95rem',
                    fontWeight: isActive ? 600 : 500,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
        
        {/* Logout Button */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            color: '#ef4444',
            borderColor: '#ef4444',
            '&:hover': {
              backgroundColor: '#fef2f2',
              borderColor: '#dc2626',
            },
            fontSize: '0.875rem',
            fontWeight: 'medium',
            py: 1,
          }}
        >
          Logout
        </Button>
        
        <Typography
          variant="body2"
          sx={{
            color: '#94a3b8',
            textAlign: 'center',
            fontSize: '0.7rem',
            mt: 1,
          }}
        >
          © 2024 MIVPS
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            height: '100vh',
            borderRight: '1px solid #e2e8f0',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer - Only render when open */}
      {open && (
        <Drawer
          variant="persistent"
          anchor="left"
          open={open}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              height: '100vh',
              borderRight: '1px solid #e2e8f0',
              backgroundColor: 'white',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
